import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import api from "lib/api/api";

const emptyEmail = {
  subject: "",
  messageEn: "",
  messageDe: "",
  messageAr: "",
  linkUrl: "",
  linkTextEn: "View course",
  linkTextDe: "Kurs ansehen",
  linkTextAr: "عرض الدورة",
};

const generatedEmail = (offering) => {
  const course = offering?.level || "German";
  return {
    subject: `${course} course announcement | ALGermanist`,
    messageEn: `Hello {name},\n\nYou previously expressed interest in our ${course} German course. We are pleased to let you know that this course is now available. You can view the course information and register using the link below.\n\nALGermanist Team`,
    messageDe: `Hallo {name},\n\ndu hast dich zuvor für unseren Deutschkurs ${course} interessiert. Wir freuen uns, dir mitteilen zu können, dass dieser Kurs jetzt verfügbar ist. Über den folgenden Link kannst du die Kursinformationen ansehen und dich anmelden.\n\nALGermanist Team`,
    messageAr: `مرحبًا {name}،\n\nلقد أبديت اهتمامك سابقًا بدورة اللغة الألمانية ${course}. يسعدنا إبلاغك بأن هذه الدورة متاحة الآن. يمكنك الاطلاع على معلومات الدورة والتسجيل من خلال الرابط أدناه.\n\nفريق ALGermanist`,
    linkUrl: `${window.location.origin}/course-interest/${offering?.slug || ""}`,
    linkTextEn: "View course",
    linkTextDe: "Kurs ansehen",
    linkTextAr: "عرض الدورة",
  };
};

const CourseInterestEmailGenerator = () => {
  const [offerings, setOfferings] = useState([]);
  const [offeringId, setOfferingId] = useState("");
  const [interests, setInterests] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [email, setEmail] = useState(emptyEmail);
  const [loading, setLoading] = useState(true);
  const [loadingInterests, setLoadingInterests] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    api.get("/admin/course-interest-offerings")
      .then((response) => setOfferings(response.data || []))
      .catch((error) => setMessage({ severity: "error", text: error.response?.data?.message || "Could not load course offerings." }))
      .finally(() => setLoading(false));
  }, []);

  const selectOffering = async (value) => {
    setOfferingId(value);
    setInterests([]);
    setSelectedIds([]);
    setMessage(null);
    const offering = offerings.find((item) => item.id === Number(value));
    setEmail(offering ? generatedEmail(offering) : emptyEmail);
    if (!value) return;

    setLoadingInterests(true);
    try {
      const response = await api.get("/admin/course-interest-offerings/interests", { params: { offeringId: value } });
      setInterests(response.data || []);
    } catch (error) {
      setMessage({ severity: "error", text: error.response?.data?.message || "Could not load interested users." });
    } finally {
      setLoadingInterests(false);
    }
  };

  const toggleRecipient = (id) => {
    setSelectedIds((current) => current.includes(id) ? current.filter((value) => value !== id) : [...current, id]);
  };

  const readyToPreview = offeringId && selectedIds.length > 0 && email.subject.trim()
    && email.messageEn.trim() && email.messageDe.trim() && email.messageAr.trim();

  const send = async () => {
    setSending(true);
    setMessage(null);
    try {
      const response = await api.post("/admin/settings/course-interest-announcement-email", {
        ...email,
        offeringId: Number(offeringId),
        interestIds: selectedIds,
      });
      setMessage({
        severity: response.data?.failed ? "error" : "success",
        text: `Course announcement sent to ${response.data?.sent || 0} of ${response.data?.recipients || 0} selected recipients${response.data?.failed ? `; ${response.data.failed} failed or were not attempted` : ""}.`,
      });
      if (!response.data?.failed) setPreviewOpen(false);
    } catch (error) {
      setMessage({ severity: "error", text: error.response?.data?.message || "Could not send the course announcement." });
    } finally {
      setSending(false);
    }
  };

  const previewSections = [
    { label: "English", value: email.messageEn, button: email.linkTextEn, rtl: false },
    { label: "Deutsch", value: email.messageDe, button: email.linkTextDe, rtl: false },
    { label: "العربية", value: email.messageAr, button: email.linkTextAr, rtl: true },
  ];

  return (
    <Paper sx={{ p: { xs: 2, md: 3 } }}>
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h6" fontWeight={800}>Course-interest announcement</Typography>
          <Typography variant="body2" color="text.secondary">
            Select a course and its interested users, generate the three-language email, edit it, preview it, and then send it.
          </Typography>
        </Box>
        {message && <Alert severity={message.severity}>{message.text}</Alert>}
        {loading ? <CircularProgress size={28} /> : (
          <FormControl fullWidth sx={{ maxWidth: 560 }}>
            <InputLabel>Select course</InputLabel>
            <Select value={offeringId} label="Select course" onChange={(event) => selectOffering(event.target.value)}>
              <MenuItem value="">Choose a course</MenuItem>
              {offerings.map((offering) => (
                <MenuItem key={offering.id} value={offering.id}>
                  {offering.level} · {offering.status} · {offering.interestCount} interested
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {offeringId && (
          <>
            <Box>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} gap={1} mb={1}>
                <Typography fontWeight={800}>Recipients ({selectedIds.length} selected)</Typography>
                <Stack direction="row" spacing={1}>
                  <Button size="small" onClick={() => setSelectedIds(interests.map((item) => item.id))} disabled={!interests.length}>Select all</Button>
                  <Button size="small" onClick={() => setSelectedIds([])} disabled={!selectedIds.length}>Clear</Button>
                </Stack>
              </Stack>
              {loadingInterests ? <CircularProgress size={28} /> : interests.length === 0 ? (
                <Alert severity="info">No interested users exist for this course.</Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
                  <Table stickyHeader size="small">
                    <TableHead><TableRow><TableCell padding="checkbox"/><TableCell>Name</TableCell><TableCell>Email</TableCell><TableCell>Phone</TableCell><TableCell>Residence</TableCell></TableRow></TableHead>
                    <TableBody>{interests.map((interest) => (
                      <TableRow hover key={interest.id} selected={selectedIds.includes(interest.id)} onClick={() => toggleRecipient(interest.id)} sx={{ cursor: "pointer" }}>
                        <TableCell padding="checkbox"><Checkbox checked={selectedIds.includes(interest.id)} /></TableCell>
                        <TableCell>{interest.name}</TableCell><TableCell>{interest.email}</TableCell><TableCell>{interest.phone || "-"}</TableCell><TableCell>{interest.accommodation || "-"}</TableCell>
                      </TableRow>
                    ))}</TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>

            <Stack spacing={2} sx={{ maxWidth: 900 }}>
              <Stack direction={{ xs: "column", sm: "row" }} gap={1} alignItems={{ sm: "center" }}>
                <Typography fontWeight={800} sx={{ flex: 1 }}>Email editor</Typography>
                <Button variant="outlined" onClick={() => setEmail(generatedEmail(offerings.find((item) => item.id === Number(offeringId))))}>Regenerate email</Button>
              </Stack>
              <Alert severity="info">Use <strong>{"{name}"}</strong> for the recipient’s name and <strong>{"{course}"}</strong> for the selected course level.</Alert>
              <TextField label="Email subject" value={email.subject} onChange={(event) => setEmail((current) => ({ ...current, subject: event.target.value }))} fullWidth />
              <TextField label="English message" value={email.messageEn} onChange={(event) => setEmail((current) => ({ ...current, messageEn: event.target.value }))} multiline minRows={5} inputProps={{ dir: "ltr" }} fullWidth />
              <TextField label="German message" value={email.messageDe} onChange={(event) => setEmail((current) => ({ ...current, messageDe: event.target.value }))} multiline minRows={5} inputProps={{ dir: "ltr" }} fullWidth />
              <TextField label="Arabic message" value={email.messageAr} onChange={(event) => setEmail((current) => ({ ...current, messageAr: event.target.value }))} multiline minRows={5} inputProps={{ dir: "rtl" }} fullWidth />
              <TextField label="Course link" type="url" value={email.linkUrl} onChange={(event) => setEmail((current) => ({ ...current, linkUrl: event.target.value }))} helperText="Optional; use a complete http:// or https:// link." fullWidth />
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField label="English button" value={email.linkTextEn} onChange={(event) => setEmail((current) => ({ ...current, linkTextEn: event.target.value }))} fullWidth />
                <TextField label="German button" value={email.linkTextDe} onChange={(event) => setEmail((current) => ({ ...current, linkTextDe: event.target.value }))} fullWidth />
                <TextField label="Arabic button" value={email.linkTextAr} onChange={(event) => setEmail((current) => ({ ...current, linkTextAr: event.target.value }))} inputProps={{ dir: "rtl" }} fullWidth />
              </Stack>
              <Button variant="contained" onClick={() => setPreviewOpen(true)} disabled={!readyToPreview} sx={{ alignSelf: "flex-start" }}>Preview selected-recipient email</Button>
            </Stack>
          </>
        )}
      </Stack>

      <Dialog open={previewOpen} onClose={() => !sending && setPreviewOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Preview course announcement</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle1" fontWeight={800} mb={2}>{email.subject}</Typography>
          {previewSections.map((section) => (
            <Paper key={section.label} variant="outlined" dir={section.rtl ? "rtl" : "ltr"} sx={{ p: 2, mb: 2, textAlign: section.rtl ? "right" : "left" }}>
              <Typography variant="overline">{section.label}</Typography>
              <Typography sx={{ whiteSpace: "pre-line" }}>{section.value.replaceAll("{name}", interests.find((item) => selectedIds.includes(item.id))?.name || "Student").replaceAll("{course}", offerings.find((item) => item.id === Number(offeringId))?.level || "German")}</Typography>
              {email.linkUrl && <Button component="a" href={email.linkUrl} target="_blank" rel="noreferrer" variant="contained" sx={{ mt: 2 }}>{section.button || "View course"}</Button>}
            </Paper>
          ))}
          <Alert severity="warning">This will send a real email to {selectedIds.length} selected interested user{selectedIds.length === 1 ? "" : "s"}. Duplicate email addresses are sent only once.</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)} disabled={sending}>Back to edit</Button>
          <Button variant="contained" color="warning" onClick={send} disabled={sending}>{sending ? "Sending..." : `Confirm and send to ${selectedIds.length}`}</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default CourseInterestEmailGenerator;
